/**
 * Validates and resolves a Kevel ctTargetUrl for internal navigation.
 *
 * Kevel creatives carry only the Shopware category id (e.g. "0190dfddf56a748e9f5dccbc621684f4"),
 * not a full URL — the storefront builds the technical category route from it, so creatives
 * never contain a shop domain and work on every sales channel.
 * Returns null for anything that isn't a category id (treated as no-fill, per issue #123).
 */

export interface ValidatedKevelUrl {
    /** The path to navigate to (without locale prefix, ready for formatLink + Vue router) */
    path: string
    /** Whether this is a valid, navigable URL */
    isValid: boolean
}

/** Shopware ids are 32 hex characters, without dashes. */
const SHOPWARE_ID_PATTERN = /^[0-9a-f]{32}$/i

/**
 * Validates a Kevel target (Shopware category id) and builds the category path for navigation.
 *
 * @param ctTargetUrl - The category id from the Kevel creative (e.g., "0190dfddf56a748e9f5dccbc621684f4")
 * @returns Validated URL object or null if invalid
 */
export function validateKevelTargetUrl(ctTargetUrl: string | undefined): ValidatedKevelUrl | null {
    const categoryId = ctTargetUrl?.trim()
    if (!categoryId) {
        return null
    }

    if (!SHOPWARE_ID_PATTERN.test(categoryId)) {
        console.warn('[kevel] ctTargetUrl is not a Shopware category id:', ctTargetUrl)
        return null
    }

    return {
        path: `/navigation/${categoryId.toLowerCase()}`,
        isValid: true,
    }
}
